import { useId, useState } from 'react'
import type { Member } from '@domain/state.ts'
import { es } from '../strings/es.ts'

/**
 * El mundo de cada niño: su dibujo, su color y cómo llama a sus monedas.
 *
 * Esto no es decoración. En TEA, meter el interés fuerte del niño dentro del
 * juego es el motivador más potente que existe, y el estudio lo dice desde la
 * primera versión. Lo que faltaba era la manera de hacerlo.
 *
 * Y se hace con palabras y emojis que elige la familia, no con contenido que
 * viene de fábrica, por dos razones que apuntan al mismo sitio. La primera es
 * que el personaje que le apasiona hoy es de alguien y no es nuestro. La
 * segunda, más importante: dentro de seis meses le apasionará otra cosa, y un
 * tema traído de fábrica se queda viejo mientras que un campo de texto no.
 */

/** Un puñado de colores que funcionan sobre los dos fondos de la app. */
const COLORES: readonly { hex: string; nombre: string }[] = [
  { hex: '#1c6a73', nombre: 'Verde azulado' },
  { hex: '#b8420f', nombre: 'Naranja' },
  { hex: '#6a1c73', nombre: 'Morado' },
  { hex: '#1c4a9e', nombre: 'Azul' },
  { hex: '#2a7f2a', nombre: 'Verde' },
  { hex: '#a81c4a', nombre: 'Frambuesa' },
]

export function LookSetting({
  member,
  onSave,
}: {
  member: Member
  onSave: (accent: string, emoji: string, coinWord: string) => void
}) {
  const [emoji, setEmoji] = useState(member.emoji ?? '')
  const [accent, setAccent] = useState(member.accent)
  const [coinWord, setCoinWord] = useState(member.coinWord ?? '')
  const [saved, setSaved] = useState(false)
  const emojiId = useId()
  const coinId = useId()

  const save = (): void => {
    onSave(accent, emoji.trim(), coinWord.trim())
    setSaved(true)
    setTimeout(() => {
      setSaved(false)
    }, 2000)
  }

  return (
    <section className="border-rule mt-6 border-t pt-5">
      <h3 className="m-0 mb-1 text-base font-extrabold">{es.config.look}</h3>
      <p className="text-muted mt-0 mb-4 text-sm">{es.config.lookHelp}</p>

      <label htmlFor={emojiId} className="mb-1 block text-sm font-bold">
        {es.config.lookEmoji}
      </label>
      <input
        id={emojiId}
        value={emoji}
        maxLength={8}
        onChange={(e) => {
          setEmoji(e.target.value)
        }}
        className="border-rule bg-surface mb-4 w-24 rounded-lg border-2 p-3 text-center text-2xl"
      />

      <p className="mb-2 text-sm font-bold">{es.config.lookColor}</p>
      <div className="mb-4 flex flex-wrap gap-2">
        {COLORES.map((color) => (
          <button
            key={color.hex}
            type="button"
            aria-label={color.nombre}
            aria-pressed={accent.toLowerCase() === color.hex}
            onClick={() => {
              setAccent(color.hex)
            }}
            className={`h-11 w-11 rounded-full border-4 ${
              accent.toLowerCase() === color.hex ? 'border-ink' : 'border-transparent'
            }`}
            style={{ background: color.hex }}
          />
        ))}
      </div>

      <label htmlFor={coinId} className="mb-1 block text-sm font-bold">
        {es.config.lookCoinWord}
      </label>
      <input
        id={coinId}
        value={coinWord}
        maxLength={20}
        onChange={(e) => {
          setCoinWord(e.target.value)
        }}
        className="border-rule bg-surface mb-1 w-full rounded-lg border-2 p-3"
      />
      <p className="text-muted mt-0 mb-4 text-sm">{es.config.lookCoinWordHelp}</p>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          className="bg-teal rounded-lg px-5 py-3 font-bold text-white"
        >
          {es.config.lookSave}
        </button>
        {saved ? (
          <span role="status" className="text-mint text-sm font-bold">
            {es.config.saved}
          </span>
        ) : null}
      </div>
    </section>
  )
}
